// About — the philosophy behind the Command Centre, plus SYM (the symmetry
// roll-up that's deliberately NOT on the work surfaces). Lives here so the
// working surfaces can stay terse; anyone who wants to read the reasoning,
// or see how supply and demand reconcile in one number, finds it here.
// Source of the framing: docs/Bvndle_Coin_KPI_Structure_with_preamble.md.

import { useCallback, useEffect, useState } from "react";

import { listActions, type ActionRow } from "../lib/actions";
import { useAuth } from "../components/AuthGate";
import { SymmetryChart } from "../components/SymmetryChart";

export function AboutPage() {
  const { session } = useAuth();
  const currentUser = session?.email ?? "unknown";
  const [actions, setActions] = useState<ActionRow[]>([]);

  const refreshActions = useCallback(async () => {
    setActions(await listActions());
  }, []);

  useEffect(() => {
    void refreshActions();
  }, [refreshActions]);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__meta">ABOUT · WHY THIS EXISTS</div>
          <h1>The five-field shape</h1>
        </div>
      </div>

      <div style={prose}>
        <p style={ethos}>
          <b>No surprise updates. No moving goalposts. No accountability gaps.</b>
        </p>

        <p>
          Every initiative carries the same{" "}
          <span style={accent}>five-field shape</span> · <b>value indicators</b>,{" "}
          <b>how we're tracking</b>, <b>who's responsible</b>, <b>blockers</b>,{" "}
          <b>what we're doing about it</b>. The review surface speaks for itself, and
          accountability doesn't evaporate between meetings.
        </p>

        <h2 style={h2}>Why symmetry, not volume</h2>
        <p>
          A healthy coin economy is measured by symmetry between issuance and redemption
          over time, not absolute volume. A million coins issued tells you nothing on its
          own · what matters is whether those coins are being spent, in what categories,
          and how fast they cycle. Supply and demand answer the same three questions ·
          coverage, attractiveness, cost · so asymmetry shows at a glance.
        </p>

        <h2 style={h2}>The seven KPIs</h2>
        <p style={{ marginBottom: 8 }}>
          <b style={{ color: "var(--purple)" }}>SYM</b> · the hero. Coin economy in
          balance. Reads once reconciliation and settlement primitives are live.
        </p>
        <p style={{ marginBottom: 8 }}>
          <b style={{ color: "var(--purple)" }}>S1·S2·S3</b> · supply / issuance. Must-win
          partner coverage on the issuance side, merchant attractiveness, cost of
          issuance. Coins enter circulation.
        </p>
        <p>
          <b style={{ color: "var(--pink)" }}>D1·D2·D3</b> · demand / redemption.
          Must-win partner coverage on the redemption side, redemption attractiveness,
          settlement velocity. Coins leave circulation.
        </p>

        <h2 style={h2}>How a review uses these surfaces</h2>
        <p>
          The two work surfaces are <b>Supply</b> and <b>Demand</b>. Each one stacks its
          three KPIs top-to-bottom, with live charts, must-win category drill-downs, and
          vendor-level commitments visible in line. When the head of redemption says
          "we're stuck onboarding Shell," someone drills into <b>Demand → S1-D1 paired
          partner coverage → Fuel → Shell</b> and the actions, owner, blockers, and
          by-when sit right there. Symmetry between supply and demand is the meta-read ·
          when they fall out of step, every category drill makes it obvious.
        </p>

        <p style={signoff}>
          Powered by <b>VFD Group</b> · Built for the Bvndle ManCo.
        </p>
      </div>

      <div style={symWrap}>
        <div style={symHead}>
          <div style={symEyebrow}>SYM · DIAGNOSTIC ROLL-UP</div>
          <div style={symTitle}>Is the coin economy in balance?</div>
          <div style={symSubtitle}>
            The symmetry read · earn vs. spend per category. Not on the work surfaces because
            it isn't actionable per se; it's the meta-question that emerges from S1·S2·S3 and
            D1·D2·D3. Use it to sanity-check whether the per-block commitments add up to a
            healthy economy.
          </div>
        </div>
        <SymmetryChart
          kpiId="sym"
          actions={actions}
          currentUser={currentUser}
          onCommitmentChanged={refreshActions}
        />
      </div>
    </>
  );
}

const prose: React.CSSProperties = {
  maxWidth: 760,
  fontSize: 14,
  lineHeight: 1.7,
  color: "var(--ink-dim)",
};

const ethos: React.CSSProperties = {
  fontSize: 16,
  color: "var(--ink)",
  marginTop: 6,
  marginBottom: 18,
  lineHeight: 1.5,
};

const accent: React.CSSProperties = {
  color: "var(--pink)",
  fontStyle: "italic",
  fontWeight: 600,
};

const h2: React.CSSProperties = {
  marginTop: 30,
  marginBottom: 10,
  fontSize: 13,
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
  fontWeight: 700,
};

const signoff: React.CSSProperties = {
  marginTop: 36,
  paddingTop: 18,
  borderTop: "1px solid var(--edge)",
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
};

const symWrap: React.CSSProperties = {
  marginTop: 32,
  paddingTop: 22,
  borderTop: "1px solid var(--edge)",
};
const symHead: React.CSSProperties = {
  marginBottom: 14,
  padding: "0 18px",
};
const symEyebrow: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.16em",
  color: "var(--purple)",
  fontWeight: 700,
  textTransform: "uppercase",
  marginBottom: 6,
};
const symTitle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: "var(--ink)",
  marginBottom: 6,
};
const symSubtitle: React.CSSProperties = {
  fontSize: 13,
  color: "var(--ink-dim)",
  lineHeight: 1.6,
  maxWidth: 760,
};
